import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/core/ui';
import type { Icon } from '@/core/ui/icons';

interface MenuProps {
  /** Nome do botão para leitores de tela. */
  label: string;
  trigger: ReactNode;
  triggerClassName?: string;
  /** `menu`: lista de ações; `dialog`: conteúdo livre (versões). */
  kind?: 'menu' | 'dialog';
  align?: 'left' | 'right';
  panelClassName?: string;
  children: (close: () => void) => ReactNode;
}

/** Botão que abre um painel flutuante; clique fora ou Esc fecha. */
export function Menu({
  label,
  trigger,
  triggerClassName,
  kind = 'menu',
  align = 'right',
  panelClassName,
  children,
}: MenuProps) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !root.current?.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        aria-label={label}
        title={label}
        aria-haspopup={kind}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={triggerClassName}
      >
        {trigger}
      </button>
      {open && (
        <div
          role={kind}
          aria-label={label}
          className={cn(
            'absolute z-40 mt-2 min-w-52 overflow-hidden rounded-2xl bg-surface py-1.5 shadow-lg ring-1 ring-line-strong',
            align === 'right' ? 'right-0' : 'left-0',
            panelClassName,
          )}
        >
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}

export function MenuItem({
  icon: IconCmp,
  children,
  onSelect,
  disabled,
}: {
  icon?: Icon;
  children: ReactNode;
  onSelect: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onSelect}
      className="flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-sm text-fg-muted transition-colors hover:bg-surface-2 hover:text-fg disabled:opacity-50"
    >
      {IconCmp && <IconCmp aria-hidden className="size-4 shrink-0" />}
      {children}
    </button>
  );
}
